import { FormControl, InputLabel, makeStyles, MenuItem, Select, Theme } from '@material-ui/core';
import React from 'react';
import { useDispatch } from 'react-redux';

import { searchFilterChanged } from '../../../actions/searchFilterChanged';
import { searchSortOrderChanged } from '../../../actions/searchSortOrderChanged';
import {
  FilterPublishStatus,
  SearchDocumentType,
  SearchSortOrder,
  TasksOrder,
  TaskType,
} from '../../../graphql/gen/types';
import { labelSelect } from '../../../models/filters/actions/labelSelect';
import { selectPublishStatusFilter } from '../../../models/filters/actions/selectPublishStatusFilter';
import { selectTasksItemTypeFilter } from '../../../models/filters/actions/selectTasksItemTypeFilterAction';
import { selectTasksOrderFilter } from '../../../models/filters/actions/selectTasksOrderFilterAction';
import { useFiltersState } from '../../../models/filters/useFiltersState';
import { useSearchFilters } from '../../../redux';
import { useSearchSortOrder } from '../../../redux';
import { spacings } from '../../../theme';
import { usePlexRoute } from '../details/usePlexRoute';
import { ALL_DOCUMENT_TYPES } from '../search/useSearch';
import { LabelInput } from './LabelInput';

const VisibilityPicker = React.memo(
  ({
    onChangeVisibility,
    visibility,
  }: {
    onChangeVisibility: (value: FilterPublishStatus) => void;
    visibility: FilterPublishStatus;
  }) => {
    const classes = useStyles();
    const items = [
      { text: 'All', filter: FilterPublishStatus.All },
      { text: 'Published', filter: FilterPublishStatus.Published },
      { text: 'Team', filter: FilterPublishStatus.Internal },
      { text: 'Drafts', filter: FilterPublishStatus.Draft },
    ];

    const handlePublishStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      onChangeVisibility(event.target.value as FilterPublishStatus);
    };
    return (
      <FormControl className={classes.select} variant="outlined" margin="dense">
        <InputLabel id="filter-visibility-label">Visibility</InputLabel>
        <Select
          labelId="filter-visibility-label"
          id="filter-visibility-select"
          value={visibility}
          onChange={handlePublishStatusChange}
          label="Visibility"
          className={classes.select}
          inputProps={{ className: classes.selectInput }}
        >
          {items.map((i) => (
            <MenuItem value={i.filter} key={i.text}>
              {i.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
);

const TasksItemTypePicker = React.memo(function TasksItemTypePicker({
  onChange,
  value,
}: {
  onChange: (value: TaskType | undefined) => void;
  value: TaskType | undefined;
}) {
  const classes = useStyles();
  const items = [
    { label: 'All', value: 'all' },
    { label: 'Milestones', value: TaskType.Milestone },
    { label: 'Tasks', value: TaskType.Task },
  ];

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    if (event.target.value === 'all') {
      onChange(undefined);
    } else {
      onChange(event.target.value as TaskType);
    }
  };
  return (
    <FormControl className={classes.select} variant="outlined" margin="dense">
      <InputLabel id="filter-visibility-label">Type</InputLabel>
      <Select
        labelId="filter-visibility-label"
        id="filter-visibility-select"
        value={value || 'all'}
        onChange={handleChange}
        label="Visibility"
        className={classes.select}
        inputProps={{ className: classes.selectInput }}
      >
        {items.map((i) => (
          <MenuItem value={i.value} key={i.value}>
            {i.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

const TasksSortOrderPicker = React.memo(function TasksSortOrderPicker({
  onChange,
  value,
}: {
  onChange: (value: TasksOrder | undefined) => void;
  value: TasksOrder | undefined;
}) {
  const classes = useStyles();
  const items = [
    { label: 'Due Date - Ascending', value: TasksOrder.DueDateAsc },
    { label: 'Due Date - Descending', value: TasksOrder.DueDateDesc },
    { label: 'Status - Ascending', value: TasksOrder.StatusAsc },
    { label: 'Status - Descending', value: TasksOrder.StatusDesc },
    { label: 'Created Date - Ascending', value: TasksOrder.CreatedAtAsc },
    { label: 'Created Date - Descending', value: TasksOrder.CreatedAtDesc },
  ];

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    if (event.target.value === TasksOrder.DueDateAsc) {
      onChange(undefined);
    } else {
      onChange(event.target.value as TasksOrder);
    }
  };
  return (
    <FormControl className={classes.select} variant="outlined" margin="dense">
      <InputLabel id="filter-visibility-label">Sort by</InputLabel>
      <Select
        labelId="filter-visibility-label"
        id="filter-visibility-select"
        value={value}
        onChange={handleChange}
        label="Visibility"
        className={classes.select}
        inputProps={{ className: classes.selectInput }}
      >
        {items.map((i) => (
          <MenuItem value={i.value} key={i.value}>
            {i.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

const searchFiltersItems = [
  { text: 'All', filter: ALL_DOCUMENT_TYPES },
  { text: 'Post', filter: [SearchDocumentType.Post] },
  { text: 'Card', filter: [SearchDocumentType.Question] },
  { text: 'Event', filter: [SearchDocumentType.Event] },
  { text: 'Milestone', filter: [SearchDocumentType.Milestone] },
  { text: 'Task', filter: [SearchDocumentType.Task] },
];

const SearchItemTypePicker = React.memo(() => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const searchFilters = useSearchFilters();
  const filterIndex = searchFiltersItems.map((item) => item.filter).indexOf(searchFilters);
  const selectedFilter = filterIndex >= 0 ? searchFiltersItems[filterIndex] : searchFiltersItems[0];

  const handleItemTypeChanged = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(searchFilterChanged(event.target.value as SearchDocumentType[]));
  };

  return (
    <FormControl className={classes.select} variant="outlined" margin="dense">
      <InputLabel id="filter-visibility-label">Item Type</InputLabel>
      <Select
        labelId="filter-visibility-label"
        id="filter-visibility-select"
        value={selectedFilter.filter}
        onChange={handleItemTypeChanged}
        label="Item Type"
        className={classes.select}
        inputProps={{ className: classes.selectInput }}
      >
        {searchFiltersItems.map((i) => (
          <MenuItem value={i.filter} key={i.text}>
            {i.text}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

const searchSortOrders = [
  { order: SearchSortOrder.Score, text: 'Most relevant' },
  { order: SearchSortOrder.UpdatedAt, text: 'Most recent' },
];

export const SearchSortOrderPicker = React.memo(() => {
  const searchSortOrder = useSearchSortOrder();
  const dispatch = useDispatch();
  const classes = useStyles();
  return (
    <FormControl className={classes.select} variant="outlined" margin="dense">
      <InputLabel>Sort by</InputLabel>
      <Select
        value={searchSortOrder}
        label="Sort by"
        onChange={(event): void => {
          dispatch(searchSortOrderChanged(event.target.value as SearchSortOrder));
        }}
        className={classes.select}
        inputProps={{ className: classes.selectInput }}
      >
        {searchSortOrders.map((option: { order: SearchSortOrder; text: string }) => {
          return (
            <MenuItem key={option.order} value={option.order}>
              {option.text}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
});

export const FilterPicker = React.memo(() => {
  const classes = useStyles();
  const [plexRoute] = usePlexRoute();
  const [filtersState, dispatch] = useFiltersState();
  const pickedPublishStatus = filtersState.publishStatusFilter;
  const pickedLabelIds = filtersState.labelIds;

  return (
    <div className={classes.container}>
      {plexRoute.page !== 'search' && (
        <VisibilityPicker
          visibility={pickedPublishStatus}
          onChangeVisibility={(visibility: FilterPublishStatus) =>
            dispatch(selectPublishStatusFilter({ pickedPublishStatusFilter: visibility }))
          }
        />
      )}
      {plexRoute.page === 'search' && <SearchItemTypePicker />}
      {plexRoute.page === 'search' && <SearchSortOrderPicker />}
      <LabelInput
        selectedLabelIds={pickedLabelIds}
        onLabelsListChanged={(labelIds: string[]) => dispatch(labelSelect({ labelIds }))}
        disableCreateNewLabel
        variant="outlined"
        className={classes.labels}
        centeredLabel
      />
      {plexRoute.page === 'tasks' && (
        <TasksItemTypePicker
          onChange={(itemType: TaskType | undefined) => dispatch(selectTasksItemTypeFilter({ itemType }))}
          value={filtersState.tasks.itemType}
        />
      )}
      {plexRoute.page === 'tasks' && (
        <TasksSortOrderPicker
          onChange={(order: TasksOrder | undefined) => dispatch(selectTasksOrderFilter({ order }))}
          value={filtersState.tasks.order}
        />
      )}
    </div>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    flexShrink: 0,
  },
  selectInput: {
    height: 25,
    minWidth: 80,
    marginTop: 5,
  },
  labels: {
    flexGrow: 2,
    minWidth: 250,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',

    paddingRight: spacings.normal,

    // Set spacing between consecutive children
    marginLeft: -spacings.normal,
    '& > *': {
      marginLeft: spacings.normal,
    },
  },
  text: {
    fontWeight: 500,
    flexShrink: 0,
    letterSpacing: '0.15px',
  },
}));
